import { NgModule, isDevMode, APP_INITIALIZER, ErrorHandler } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { RouteReuseStrategy } from '@angular/router';

import { AlertController, IonicModule, IonicRouteStrategy } from '@ionic/angular';

import { AppComponent } from './app.component';
import { OfflineComponent } from './offline/offline.component'; // standalone component
import { AppRoutingModule } from './app-routing.module';
import { GlobalErrorHandler } from './core/global-error.handler';

import { registerLocale } from '@abp/ng.core/locale';
import { CoreModule, provideAbpCore, withOptions, ConfigStateService, AbpApplicationConfigurationService } from '@abp/ng.core';
import { FALLBACK_APP_CONFIG } from './app.config-fallback';
import { environment } from 'src/environments/environment';
import { TenantInterceptor } from './services/tenant.interceptor';
import { AuthInterceptor } from './services/auth.interceptor';
import { provideAbpOAuth } from '@abp/ng.oauth';
import { OAuthService } from 'angular-oauth2-oidc';
// Optional: resilient OAuth discovery (prevents uncaught errors on offline)
export function safeOAuthInitializer(oauthService: OAuthService)
{
	return () =>
	{
		if (!navigator.onLine)
		{
			console.warn('[safeOAuthInitializer] Skipping OAuth discovery while offline');
			return Promise.resolve();
		}

		oauthService.events.subscribe(e =>
		{
			if (e.type === 'token_error')
			{
				console.warn('Token refresh failed');

				const error = e as any;

				if (error?.reason?.status === 0)
				{
					// 🚫 Network failure → DO NOT redirect
					console.warn('Network issue during token refresh - staying offline');
					return;
				}
			}
		});

		return oauthService.loadDiscoveryDocumentAndTryLogin().catch(err =>
		{
			console.warn('[safeOAuthInitializer] OAuth discovery failed:', err);
		});
	};
}
import { IonicStorageModule } from '@ionic/storage-angular';
import { ServiceWorkerModule } from '@angular/service-worker';

// Safe ABP configuration loader to prevent white screen if API is down
export function safeConfigLoader(appConfigSvc: AbpApplicationConfigurationService, configState: ConfigStateService)
{
	// Synchronous fallback to avoid blank screen while network calls fail.
	return () =>
	{
		configState.setState(FALLBACK_APP_CONFIG);

		appConfigSvc
			.get({ includeLocalizationResources: true })
			.subscribe({
				next: cfg =>
				{
					if (cfg)
					{
						configState.setState(cfg);
					} else
					{
						console.warn('[safeConfigLoader] Empty config received; keeping fallback');
					}
				},
				error: err =>
				{
					console.error('[safeConfigLoader] Network/config fetch failed; keeping fallback:', err);

					//check if offline
					if (!navigator.onLine)
					{
						//show an alert, and ok will refresh the page
						let alertController = new AlertController();
						alertController
							.create({
								header: 'Offline',
								message: 'You are currently offline. Please check your internet connection.',
								buttons: [
									{
										text: 'OK',
										handler: () =>
										{
											window.location.reload();
										}
									}
								]
							})
							.then(alert =>
							{
								alert.present();
							});
						return;
					}
					// Handle 404 errors here
					if (err?.status === 404)
					{
						console.warn('[safeConfigLoader] Authentication error detected, clearing session');
						Object.keys(localStorage).forEach(key =>
						{
							if (key.includes('abp') || key.includes('oauth') || key.includes('oidc'))
							{
								localStorage.removeItem(key);
							}
						});
						setTimeout(() =>
						{
							window.location.href = '/';
						}, 100);
					}
				}
			});
		return Promise.resolve();
	};
}

@NgModule({
	declarations: [AppComponent],
	imports: [
		BrowserModule,
		HttpClientModule,
		IonicStorageModule.forRoot(),
		IonicModule.forRoot({
			innerHTMLTemplatesEnabled: true
		}),
		OfflineComponent,
		AppRoutingModule,
		ServiceWorkerModule.register('ngsw-worker.js', {
			enabled: !isDevMode(),
			registrationStrategy: 'registerWhenStable:30000'
		})
	],
	providers: [
		{ provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
		provideAbpCore(
			withOptions({
				environment,
				registerLocaleFn: registerLocale(),
			}),
		),

		provideAbpOAuth(),

		// Add safe configuration loader
		{
			provide: APP_INITIALIZER,
			useFactory: safeConfigLoader,
			deps: [AbpApplicationConfigurationService, ConfigStateService],
			multi: true,
		},
		{
			provide: APP_INITIALIZER,
			useFactory: safeOAuthInitializer,
			deps: [OAuthService],
			multi: true,
		},
		// Global error handler to route to /offline on network/server errors
		{ provide: ErrorHandler, useClass: GlobalErrorHandler },
		{ provide: HTTP_INTERCEPTORS, useClass: TenantInterceptor, multi: true },
		{ provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
	],
	bootstrap: [AppComponent],
})
export class AppModule { }
